import { useEffect, useCallback } from 'react'
import { useInboxStore } from '../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../services/actions/inbox/useInboxHandlers'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import { openInboxBulkTags } from './openInboxBulkTags'

const isTyping = () => {
    const el = document.activeElement
    if (!el) return false
    const tag = el.tagName.toLowerCase()
    return tag === 'input' || tag === 'textarea' || (el as HTMLElement).isContentEditable
}

export const useInboxKeyboard = (onShowShortcuts: () => void) => {
    const { store: inboxStore } = useInboxStore()
    const {
        conversations,
        filteredConversations,
        selectedConversation,
        selectedConnection,
        pinned, starred, tags, archived, readed, query, lastMessagedFrom, lastMessagedTo,
    } = inboxStore

    const {
        openConversation,
        archiveConversationHandler,
        starConversationHandler,
        pinConversationHandler,
        readConversationHandler,
        openNewMessagePage,
        setApplyTemplateHandler,
        visitParticipantLinkedInProfileHandler,
        visitParticipantLinkedInActivityFeedHandler,
        snoozeConversationHandler,
    } = useInboxHandlers()

    const { setChooseTemplateModal, setBulkEditTagsModal, showInfoToast } = useAppHandlers()

    const handleKey = useCallback((e: KeyboardEvent) => {
        if (isTyping()) return

        // Z / Ctrl+Z → snooze or unsnooze (handled before modifier-key block)
        if (e.key === 'z' && !e.metaKey && !e.altKey && !e.shiftKey) {
            if (selectedConversation) {
                e.preventDefault()
                const isSnoozed = !!(selectedConversation.snoozedUntil && selectedConversation.snoozedUntil > Date.now())
                if (isSnoozed) {
                    snoozeConversationHandler(selectedConversation.conversationId, null)
                } else {
                    const btn = document.querySelector(`[data-conv-id="${selectedConversation.conversationId}"] [data-snooze-btn]`) as HTMLButtonElement
                    btn?.click()
                }
            }
            return
        }

        if (e.metaKey || e.ctrlKey || e.altKey) return

        const filtersApplied =
            pinned !== 'All' || starred !== 'All' || tags.length !== 0 ||
            archived !== 'No' || readed !== 'All' || query.length > 0 ||
            lastMessagedFrom !== '' || lastMessagedTo !== ''

        const displayList: any[] = filtersApplied
            ? (filteredConversations ?? [])
            : conversations

        const currentIdx = selectedConversation
            ? displayList.findIndex((c: any) => c.conversationId === selectedConversation.conversationId)
            : -1

        switch (e.key) {
            case 'j': {
                const prev = displayList[currentIdx - 1]
                if (!prev) break
                e.preventDefault()
                openConversation(prev)
                requestAnimationFrame(() => {
                    document.querySelector(`[data-conv-id="${prev.conversationId}"]`)
                        ?.scrollIntoView({ block: 'nearest' })
                })
                break
            }
            case 'k': {
                const next = displayList[currentIdx + 1]
                if (!next) break
                e.preventDefault()
                openConversation(next)
                requestAnimationFrame(() => {
                    document.querySelector(`[data-conv-id="${next.conversationId}"]`)
                        ?.scrollIntoView({ block: 'nearest' })
                })
                break
            }
            case 'n': {
                const next = displayList[currentIdx + 1]
                if (!next) break
                e.preventDefault()
                openConversation(next)
                requestAnimationFrame(() => {
                    document.querySelector(`[data-conv-id="${next.conversationId}"]`)
                        ?.scrollIntoView({ block: 'nearest' })
                })
                window.dispatchEvent(new CustomEvent('inbox:ai-compose'))
                break
            }
            case 'b': {
                const prev = displayList[currentIdx - 1]
                if (!prev) break
                e.preventDefault()
                openConversation(prev)
                requestAnimationFrame(() => {
                    document.querySelector(`[data-conv-id="${prev.conversationId}"]`)
                        ?.scrollIntoView({ block: 'nearest' })
                })
                window.dispatchEvent(new CustomEvent('inbox:ai-compose'))
                break
            }
            case 'e': {
                if (!selectedConversation) break
                e.preventDefault()
                archiveConversationHandler({
                    id: selectedConversation._id,
                    conversationId: selectedConversation.conversationId,
                    isArchived: !selectedConversation.isArchived,
                })
                break
            }
            case 'g':
            case 'G': {
                if (!selectedConversation) break
                e.preventDefault()
                window.dispatchEvent(new CustomEvent('inbox:ai-compose-now'))
                break
            }
            case 's': {
                if (!selectedConversation) break
                e.preventDefault()
                starConversationHandler({
                    conversationId: selectedConversation.conversationId,
                    isStarred: !selectedConversation.isStarred,
                })
                break
            }
            case 'p': {
                if (!selectedConversation) break
                e.preventDefault()
                pinConversationHandler({
                    conversationId: selectedConversation.conversationId,
                    isPinned: !selectedConversation.isPinned,
                })
                break
            }
            case 'i': {
                if (!selectedConversation) break
                e.preventDefault()
                readConversationHandler(selectedConversation, !selectedConversation.isRead)
                break
            }
            case 'c': {
                e.preventDefault()
                openNewMessagePage()
                break
            }
            case 'r': {
                if (!selectedConversation) break
                e.preventDefault()
                const textarea = document.querySelector('[data-inbox-reply]') as HTMLTextAreaElement
                textarea?.focus()
                break
            }
            case 't':
            case 'T': {
                if (!selectedConversation) break
                e.preventDefault()
                setChooseTemplateModal((msg: string) => setApplyTemplateHandler({ content: msg }))
                break
            }
            case 'h':
            case 'H': {
                e.preventDefault()
                window.dispatchEvent(new CustomEvent('inbox:attach-file'))
                break
            }
            case '#': {
                if (!selectedConversation) break
                e.preventDefault()
                const connectionId = selectedConnection?._id || selectedConversation?.connectionId
                const snapshotTags =
                    (Array.isArray(selectedConversation?.tags) && selectedConversation.tags) ||
                    (Array.isArray(selectedConnection?.tags) && selectedConnection.tags) ||
                    []
                openInboxBulkTags(connectionId, snapshotTags, setBulkEditTagsModal, showInfoToast)
                break
            }
            case 'l':
            case 'L': {
                if (!selectedConversation) break
                if (selectedConversation.participants?.length !== 1) break
                const participant = selectedConversation.participants[0]
                if (!participant?.publicIdentifier) break
                e.preventDefault()
                visitParticipantLinkedInProfileHandler(participant)
                break
            }
            case 'f':
            case 'F': {
                if (!selectedConversation) break
                if (selectedConversation.participants?.length !== 1) break
                const participant = selectedConversation.participants[0]
                if (!participant?.publicIdentifier) break
                e.preventDefault()
                visitParticipantLinkedInActivityFeedHandler(participant)
                break
            }
            case '?': {
                e.preventDefault()
                onShowShortcuts()
                break
            }
            default:
                break
        }
    }, [
        conversations, filteredConversations, selectedConversation, selectedConnection,
        pinned, starred, tags, archived, readed, query, lastMessagedFrom, lastMessagedTo,
        openConversation, archiveConversationHandler, starConversationHandler,
        pinConversationHandler, readConversationHandler, openNewMessagePage,
        setApplyTemplateHandler, setChooseTemplateModal, setBulkEditTagsModal, showInfoToast,
        visitParticipantLinkedInProfileHandler, visitParticipantLinkedInActivityFeedHandler,
        snoozeConversationHandler, onShowShortcuts,
    ])

    useEffect(() => {
        document.addEventListener('keydown', handleKey)
        return () => document.removeEventListener('keydown', handleKey)
    }, [handleKey])
}
